<template>
  <div class="gift-card-edit-container">
    <div class="gift-card-feedback feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
      <transition name="fade" @enter="enterSuccess">
        <FeedbackSuccess
          v-if="showableSuccessFeedback"
          :message="successMessage"
        />
      </transition>
    </div>
    <PageHeadlineWithFourSteps
      class="gift-card__headline__title"
      title="ギフトカード作成"
      :steps="flowLineContents.steps"
      :active-step="flowLineContents.activeStep"
    />
    <div class="gift-card__gray-border" />
    <div v-if="isGiftCardSessionLoading" class="gift-card-session-loading">
      <Loading />
    </div>
    <div v-else class="gift-card__body">
      <div v-if="wishListUserNickname">
        <div class="gift-card__receiver-info-area-wrapper">
          <div class="gift-card__receiver-info-area">
            <AccountIconWh40
              class="gift-card__account-icon"
              :image-url="wishListUserIconImageUrl"
            />
            <div class="gift-card__receiver-name-area">
              <div class="gift-card__receiver-name body-bold">
                {{ wishListUserNickname }}
              </div>
              <div class="gift-card__message-suffix module-text">
                さんへのギフト
              </div>
            </div>
          </div>
        </div>
        <div class="gift-card__gray-border" />
      </div>
      <form
        :action="postUrl"
        method="POST"
        accept-charset="UTF-8"
        name="gift_card_edit_form"
      >
        <input name="utf8" type="hidden" value="✓" />
        <input
          type="hidden"
          name="authenticity_token"
          :value="authenticityToken"
        />
        <div class="gift-card__main-wrapper">
          <div
            v-if="boxListErrorMessages.length >= 1"
            class="gift-card__alert-box-area"
          >
            <div class="gift-card__alert-box box-list--alert">
              <div
                v-for="(boxListErrorMessage, index) in boxListErrorMessages"
                :key="index"
                class="error_message"
              >
                {{ boxListErrorMessage }}
              </div>
            </div>
          </div>
          <div class="gift-card__required_caution require caption">必須</div>
          <div class="gift-card__main">
            <div class="gift-card__main-left">
              <div class="gift-card__card-design">
                <div
                  id="select-card-design-area"
                  class="gift-card__card-design-area"
                >
                  <h2
                    id="select-card-design-title"
                    class="gift-card__first_title require title"
                  >
                    1. カードデザインを選ぶ
                  </h2>
                  <div
                    v-if="cardDesignId"
                    id="select-card-design-content"
                    class="gift-card__card-design-content"
                  >
                    <input
                      id="gift_card_form_card_design_id"
                      type="hidden"
                      :value="cardDesignId"
                      name="gift_card_form[card_design_id]"
                    />
                    <ImgLazy
                      :image-url="cardDesignImageUrl"
                      alt-text="カードデザイン"
                      aspect-ratio="1"
                      class="gift-card__card-design-image"
                    />
                    <div class="gift-card__card-design-change-link">
                      <button
                        class="text-link"
                        value="change_card_design"
                        name="gift_card_form[submit_type]"
                        @click="onClickChangeCardDesign"
                      >
                        <span class="gift-card__sync-outline"> 変更する </span>
                      </button>
                    </div>
                  </div>
                  <div
                    v-else
                    id="select-card-design-content"
                    class="gift-card__card-design-content"
                  >
                    <SelectButtonArea
                      id="select-card-design--select"
                      name="gift_card_form[submit_type]"
                      value="change_card_design"
                      label="カードデザイン一覧から選択する"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="gift-card__main-right">
              <div class="gift-card__input-form">
                <div
                  id="input-message-area"
                  class="gift-card__input-message-area"
                >
                  <h2 id="input-message-title" class="gift-card__title title">
                    2. メッセージを入力する（任意）
                  </h2>
                  <div class="gift-card__input-message-description minimal">
                    メッセージは300文字以内でご入力ください
                  </div>
                  <div>
                    <textarea
                      id="input-message-textarea"
                      v-model="messageAreaText"
                      :class="{
                        'input-form--inputted': isMessageAreaActive,
                        'input-form': !isMessageAreaActive,
                        'input-form--inputting': isMessageAreaActive
                      }"
                      name="gift_card_form[message]"
                      placeholder="例：
  今月もおつかれさまでした！
  ささやかですが、カフェに行った時にでも使ってください。"
                      cols="30"
                      rows="5"
                      maxlength="300"
                      @focus="onFocusMessageArea"
                      @blur="onBlurMessageArea"
                    />
                  </div>
                </div>
                <div
                  id="input-sender-name-area"
                  class="gift-card__input-sender-name-area"
                >
                  <h2
                    id="input-sender-name-title"
                    class="gift-card__title require title"
                  >
                    3. あなたのお名前を入力する
                  </h2>
                  <div class="gift-card__input-message-description minimal">
                    <strong>今回のギフトの贈り主</strong>
                    として表示されます<br />
                    20文字以内でご入力ください
                  </div>
                  <div class="gift-card__sender-name-with-icon">
                    <div
                      v-if="currentUserProfileIconUrl"
                      class="gift-card__account-icon"
                    >
                      <AccountIconWh40 :image-url="currentUserProfileIconUrl" />
                    </div>
                    <div class="gift-card__sender-name-input">
                      <InputFormText
                        id="input-sender-name-text-input"
                        v-model="senderNameText"
                        name="gift_card_form[sender_name]"
                        placeholder="あなたのお名前（20文字以内）"
                        max-length="20"
                        :rules="validateSenderName"
                        @focus="onFocusSenderName"
                        @blur="onBlurSenderName"
                      />
                    </div>
                  </div>
                  <div class="gift-card__sender-name-box-list">
                    ※ここで変更した名前はこのギフトカードのみに利用され、プロフィール内の「ニックネーム」は変更されません
                  </div>
                </div>
                <div id="select-item-area" class="gift-card__select-item-area">
                  <div class="gift-card__select-item-title-area">
                    <h2
                      id="select-item-title"
                      class="gift-card__title require title"
                    >
                      4. ギフトを選ぶ
                    </h2>
                    <a
                      class="gift-card__send-to-multiple-people__area"
                      @click="showSendToMultiplePeopleModal"
                    >
                      <AlertCircleOutline />
                      <span
                        class="gift-card__send-to-multiple-people__link-text"
                      >
                        複数人への贈り方
                      </span>
                    </a>
                    <SendToMultiplePeopleModal
                      v-if="sendToMultiplePeopleModalDisplayable"
                      :has-fulfillment-item-sku="hasFulfillmentItemSku"
                      via="gift_card_edit"
                      @clicked-modal-cover="hideSendToMultiplePeopleModal"
                      @clicked-close="hideSendToMultiplePeopleModal"
                    />
                  </div>
                  <div class="gift-card__input-message-description minimal">
                    最大10枚まで一度に購入することができます
                    <br />
                    複数枚購入した際も、発行されるギフトURLは1つです
                  </div>
                  <div
                    v-if="isItemSkuContentsLoading"
                    class="item-sku-contents-loading"
                  >
                    <SmallLoading />
                  </div>
                  <div v-else>
                    <div
                      v-if="giftCardSessionItemSkuContents.length > 0"
                      id="select-item-box-area"
                      class="gift-card__ticket-box-content"
                    >
                      <div class="gift-card__ticket-box-items">
                        <!-- eslint-disable prettier/prettier v-forの部分でvue/html-indentとの競合で警告が解決できなくなるのでdisableする -->
                        <div
                          v-for="(giftCardSessionItemSkuContent,
                                  index) in giftCardSessionItemSkuContents"
                          :id="'select-item__box--' + index"
                          :key="index"
                          class="select-item__box"
                          :class="'select-item__box--' + index"
                        >
                          <!-- eslint-enable prettier/prettier -->
                          <div class="gift-card__ticket-box-item">
                            <TicketDetail
                              :link-url="`/items/${giftCardSessionItemSkuContent.itemId}/item_skus/${giftCardSessionItemSkuContent.itemSkuId}`"
                              :image-url="
                                giftCardSessionItemSkuContent.imageUrl
                              "
                              :item-id="giftCardSessionItemSkuContent.itemId"
                              :item-name="
                                giftCardSessionItemSkuContent.itemName
                              "
                              :item-sku-id="
                                giftCardSessionItemSkuContent.itemSkuId
                              "
                              :item-sku-name="
                                giftCardSessionItemSkuContent.itemSkuName
                              "
                              :brand-name="
                                giftCardSessionItemSkuContent.brandName
                              "
                              :expired-date="
                                giftCardSessionItemSkuContent.expiredDate
                              "
                              :price="giftCardSessionItemSkuContent.price"
                              :is-ticket-disabled="false"
                              :is-including-tax="
                                giftCardSessionItemSkuContent.isIncludingTax
                              "
                            />
                          </div>
                          <div class="gift-card__ticket-box-buttons">
                            <div
                              class="delete_button"
                              @click="
                                clickDeleteButton(
                                  giftCardSessionItemSkuContent.id,
                                  giftCardSessionItemSkuContent.itemSkuId
                                )
                              "
                            >
                              削除
                            </div>
                            <div>
                              <div class="stepper-control__box">
                                <span
                                  :class="{
                                    'stepper-control__btn--minus':
                                      !giftCardSessionItemSkuContent.isMinusBtnActive,
                                    'stepper-control__btn--minus--active':
                                      giftCardSessionItemSkuContent.isMinusBtnActive
                                  }"
                                  @click="countDown(index)"
                                />
                                <span class="stepper-control__number">
                                  {{ giftCardSessionItemSkuContent.quantity }}
                                </span>
                                <input
                                  v-model="giftCardSessionItemSkuContent.id"
                                  type="hidden"
                                  :name="`gift_card_form[gift_card_session_item_sku_contents_attributes][${index}][id]`"
                                />
                                <input
                                  v-model="
                                    giftCardSessionItemSkuContent.quantity
                                  "
                                  type="hidden"
                                  :name="`gift_card_form[gift_card_session_item_sku_contents_attributes][${index}][quantity]`"
                                />
                                <span
                                  :class="{
                                    'stepper-control__btn--plus':
                                      !giftCardSessionItemSkuContent.isPlusBtnActive,
                                    'stepper-control__btn--plus--active':
                                      giftCardSessionItemSkuContent.isPlusBtnActive
                                  }"
                                  @click="countUp(index)"
                                />
                              </div>
                            </div>
                            <TextModal
                              v-if="isDeleteItemSkuModalActive"
                              :title-text="'本当に削除しますか？'"
                              :caption-text="'ギフト情報が削除されます。'"
                              :primary-button-text="'削除する'"
                              :cancel-button-text="'キャンセル'"
                              @clicked-modal-cover="closeDeleteItemSkuModal"
                              @clicked-primary="
                                onClickedPrimaryDeleteItemSkuModal
                              "
                              @clicked-cancel="closeDeleteItemSkuModal"
                            />
                          </div>
                          <div
                            v-if="
                              itemSkuError &&
                              findItemSkuError(
                                giftCardSessionItemSkuContent.itemSkuId
                              )
                            "
                            class="gift-card__ticket-box-errors"
                          >
                            <p
                              v-for="(message, i) in findItemSkuError(
                                giftCardSessionItemSkuContent.itemSkuId
                              ).message"
                              :key="i"
                            >
                              {{ message }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="
                        giftCardSessionItemSkuContents.length == 0 &&
                        !wishListUserNickname
                      "
                    >
                      <SelectButtonArea
                        id="select-item--select"
                        name="gift_card_form[submit_type]"
                        value="add_item"
                        label="ギフト一覧から追加する"
                        :errors="
                          hasNoItemSelectedError ? noItemSelectedErrors : []
                        "
                      />
                    </div>
                    <div v-else>
                      <div
                        v-if="wishListUserNickname"
                        class="gift-card__adding-gift-area--top"
                      >
                        <button
                          id="select-item--wish-list"
                          class="text-link"
                          name="gift_card_form[submit_type]"
                          value="add_from_wish_list"
                        >
                          <span class="tl-plus-circle--left">
                            ほしいものリストから追加する
                          </span>
                        </button>
                      </div>
                      <div
                        :class="`gift-card__adding-gift-area${
                          wishListUserNickname ? '--bottom' : '--top'
                        }`"
                      >
                        <button
                          id="select-item--add"
                          class="text-link"
                          name="gift_card_form[submit_type]"
                          value="add_item"
                        >
                          <span class="tl-plus-circle--left">
                            ギフト一覧から追加する
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- FIXME(gift_card_preview_optional): ABテスト決着後、不要ならこのセクション（PC幅では右カラムの一番下、SP幅ではギフトフォームの一番下に配置）を削除する -->
              <div v-if="previewOptional" class="gift-card__preview-section">
                <div
                  class="gift-card__gray-border gift-card__preview-section-border"
                />
                <UsableProfileLinkArea
                  class="gift-card__usable-profile-link-area"
                  :displayable="showUsableProfileLink"
                  :value="usableProfileLink"
                  @change="clickUsableProfileLinkSwitch"
                />
                <ButtonSecondary
                  id="show-preview-modal-button"
                  text="相手に届く画面を見てみる"
                  type="button"
                  class="gift-card__preview-button"
                  :disabled="!isPreviewAndPaymentButtonAvailable || null"
                  @click-secondary-button="showPreviewModal"
                >
                  <Eye
                    width="24"
                    height="24"
                    :icon-color="previewButtonIconColor"
                  />
                  相手に届く画面を見てみる
                </ButtonSecondary>
              </div>
            </div>
          </div>
        </div>
        <SuggestedItemSkuSection
          :item-sku-ids="giftCardSessionItemSkuIds"
          @clicked-suggested-item-sku-add-button="
            clickedSuggestedItemSkuAddButton
          "
        />
        <div class="gift-card__gray-border" />
        <div class="gift-card__foot-wrapper">
          <div class="gift-card__foot-content">
            <div id="total-price-area" class="gift-card__total-price-area">
              <div id="total-price-label" class="gift-card__total-price-label">
                <label class="gift-card__total-price-label__title">
                  決済合計
                </label>
                <label
                  v-if="isTotalIncludingTax"
                  class="gift-card__total-price-label__including-tax"
                >
                  （税込）
                </label>
              </div>
              <label id="total-price-label" class="gift-card__bold-title title">
                ¥{{ totalPrice.toLocaleString() }}
              </label>
            </div>
            <div id="save-button-area" class="gift-card__save-button-area">
              <button
                v-if="!previewOptional"
                id="save-button--preview-and-payment"
                class="btn btn--primary"
                value="preview_and_payment"
                name="gift_card_form[submit_type]"
                data-disable-with="プレビュー / 決済へ進む"
                :disabled="!isPreviewAndPaymentButtonAvailable || null"
              >
                プレビュー / 決済へ進む
              </button>
              <!-- FIXME(gift_card_preview_optional): ABテスト決着後、どちらかのブロックを削除する -->
              <template v-else>
                <ButtonPrimary
                  v-if="loggedIn"
                  id="save-button--continue"
                  text="決済へ進む"
                  value="continue"
                  name="gift_card_form[submit_type]"
                  data-disable-with="決済へ進む"
                  :disabled="!isPreviewAndPaymentButtonAvailable || null"
                  @click-primary-button="clickContinueButton"
                />
                <ButtonPrimary
                  v-else
                  id="save-button--login-and-continue"
                  text="ログイン / 決済へ進む"
                  value="continue"
                  name="gift_card_form[submit_type]"
                  data-disable-with="ログイン / 決済へ進む"
                  :disabled="!isPreviewAndPaymentButtonAvailable || null"
                  @click-primary-button="clickLoginAndContinueButton"
                />
              </template>
              <div class="gift-card__reset-link-area">
                <a
                  id="delete-draft-text-link"
                  class="text-link--sub"
                  @click.prevent="showDeleteDraftModal"
                >
                  リセットする
                </a>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div id="delete_draft_modal" class="modal">
      <TextModal
        v-if="isDeleteDraftModalActive"
        :title-text="deleteDraftModalText.title"
        :caption-text="deleteDraftModalText.caption"
        :primary-button-text="deleteDraftModalText.primaryButton"
        :cancel-button-text="deleteDraftModalText.cancelButton"
        @clicked-modal-cover="closeDeleteDraftModal"
        @clicked-primary="onClickedPrimaryDeleteDraftModal"
        @clicked-cancel="closeDeleteDraftModal"
      />
    </div>
    <!-- FIXME(gift_card_preview_optional): ABテスト決着後、不要ならこのモーダルを削除する。両カラムから共有する単一のモーダル -->
    <GiftCardPreviewModal
      v-if="previewOptional && previewModalDisplayable"
      :card-design-image-url="cardDesignImageUrl"
      :sender-name="senderNameText"
      :profile-icon-image-url="currentUserProfileIconUrl"
      :profile-link="usableProfileLink"
      :message="messageAreaText || ''"
      :item-sku-contents="giftCardSessionItemSkuContents"
      @clicked-modal-cover="hidePreviewModal"
      @clicked-close="hidePreviewModal"
    />
  </div>
</template>
<script>
import axios from 'axios'
import TextModal from '../../molecules/modals/text-modal.vue'
import PageHeadlineWithFourSteps from '../../molecules/headlines/PageHeadlineWithFourSteps.vue'
import TicketDetail from '../../atoms/tickets/TicketDetail.vue'
import Loading from '../../atoms/loadings/Loading.vue'
import SmallLoading from '../../atoms/loadings/SmallLoading.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import AccountIconWh40 from '../../atoms/account_icons/AccountIconWh40.vue'
import SuggestedItemSkuSection from '../../organism/gift_card_edit/SuggestedItemSkuSection.vue'
import AlertCircleOutline from '../../atoms/svg_icons/AlertCircleOutline.vue'
import SendToMultiplePeopleModal from '../../molecules/modals/SendToMultiplePeopleModal.vue'
import ImgLazy from '../../atoms/images/ImgLazy.vue'
import InputFormText from '../../atoms/form/InputFormText.vue'
import SelectButtonArea from '../../atoms/buttons/SelectButtonArea.vue'
import { trackAbTestImpression } from '../../../utils/ab-test-impression'
import GiftCardPreviewModal from '../../molecules/modals/gift_card_preview/GiftCardPreviewModal.vue'
import ButtonPrimary from '../../molecules/buttons/ButtonPrimary.vue'
import ButtonSecondary from '../../molecules/buttons/ButtonSecondary.vue'
import Eye from '../../atoms/svg_icons/Eye.vue'
import UsableProfileLinkArea from '../../molecules/gift_card/UsableProfileLinkArea.vue'
import {
  GIFT_CARD_FLOW_STEPS,
  GIFT_CARD_FLOW_STEPS_PREVIEW_OPTIONAL
} from '../../../constants/flowLineConstants'

export default {
  components: {
    TextModal,
    PageHeadlineWithFourSteps,
    TicketDetail, // TODO: EditableTicketに置き換える（※itemSkuErrorがある場合の追加実装必要）
    Loading,
    SmallLoading,
    FeedbackError,
    FeedbackSuccess,
    AccountIconWh40,
    SuggestedItemSkuSection,
    AlertCircleOutline,
    SendToMultiplePeopleModal,
    ImgLazy,
    GiftCardPreviewModal,
    Eye,
    ButtonPrimary,
    ButtonSecondary,
    UsableProfileLinkArea,
    InputFormText,
    SelectButtonArea
  },
  props: {
    wishListUserNickname: {
      type: String,
      required: false,
      default: ''
    },
    wishListUserIconImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    postUrl: {
      type: String,
      required: true
    },
    authenticityToken: {
      type: String,
      required: true
    },
    boxListErrorMessages: {
      type: Array,
      required: false,
      default: () => []
    },
    currentUserProfileIconUrl: {
      type: String,
      required: false,
      default: ''
    },
    currentUserName: {
      type: String,
      required: false,
      default: ''
    },
    itemSkuError: {
      type: Array,
      required: false,
      default: () => []
    },
    flashNotice: {
      type: String,
      required: false,
      default: ''
    },
    previewOptional: {
      type: Boolean,
      required: false,
      default: false
    },
    previewOptionalTestAssignment: {
      type: Object,
      required: false,
      default: null
    },
    loggedIn: {
      type: Boolean,
      required: false,
      default: false
    },
    // FIXME(gift_card_preview_optional): ABテスト決着後、previewOptionalの分岐と合わせて削除を検討する
    showUsableProfileLink: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      giftCardSessionId: null,
      cardDesignId: null,
      cardDesignImageUrl: '',
      senderNameText: '',
      messageAreaText: '',
      isMessageAreaActive: false,
      giftCardSessionItemSkuContents: [],
      totalPrice: 0,
      isDeleteDraftModalActive: false,
      isPreviewAndPaymentButtonAvailable: false,
      deleteDraftModalText: {
        title: '内容をリセットしますか？',
        caption: '現在編集中のギフトカードの内容が削除されます',
        primaryButton: 'リセットする',
        cancelButton: 'キャンセル'
      },
      // FIXME(gift_card_preview_optional): ABテスト決着後、previewOptionalの分岐を削除する
      flowLineContents: {
        steps: this.previewOptional
          ? GIFT_CARD_FLOW_STEPS_PREVIEW_OPTIONAL
          : GIFT_CARD_FLOW_STEPS,
        activeStep: 1
      },
      isGiftCardSessionLoading: false,
      isItemSkuContentsLoading: false,
      isDeleteItemSkuModalActive: false,
      selectedItemSkuContentId: null,
      selectedItemSkuId: null,
      showableErrorFeedback: false,
      errorMessage: '',
      showableSuccessFeedback: false,
      successMessage: '',
      timeoutId: '',
      sendToMultiplePeopleModalDisplayable: false,
      previewModalDisplayable: false,
      usableProfileLink: false,
      hasDeletedAllSelectedItems: false
    }
  },
  computed: {
    hasNoItemSelectedError() {
      return !this.isItemSkuContentsLoading && this.hasDeletedAllSelectedItems
    },
    noItemSelectedErrors() {
      return ['贈るギフトを選んでください']
    },
    isTotalIncludingTax() {
      return this.giftCardSessionItemSkuContents.some(
        itemSkuContent => itemSkuContent.isIncludingTax
      )
    },
    giftCardSessionItemSkuIds() {
      return this.giftCardSessionItemSkuContents.map(
        itemSkuContent => itemSkuContent.itemSkuId
      )
    },
    hasFulfillmentItemSku() {
      return (
        this.giftCardSessionItemSkuContents.length > 0 &&
        this.giftCardSessionItemSkuContents.every(
          itemSkuContent => itemSkuContent.isFulfillment
        )
      )
    },
    // FIXME(gift_card_preview_optional): ABテスト決着後、不要なら削除する
    previewButtonIconColor() {
      return this.isPreviewAndPaymentButtonAvailable ? '#F08B71' : '#888888'
    }
  },
  mounted() {
    const showableErrorMessage = document.querySelector('.error_message')
    if (showableErrorMessage) {
      this.showableErrorFeedback = true

      const isWishListError = document
        .querySelector('.error_message')
        .textContent.match(/相手のプロフィール情報に変更がありました/)
      if (isWishListError) {
        // ほしいものリストの情報をリセットする場合、ギフトカードはそのまま作成できるのでフィードバックのメッセージも合わせて変えておく。
        this.errorMessage = 'エラーが発生しました'
      } else {
        this.errorMessage = 'ギフトカードを作成出来ません'
      }
    }

    this.getGiftCardSession()
    this.getGiftCardSessionItemSkuContents()
    trackAbTestImpression(this.previewOptionalTestAssignment)

    // 贈り主名に user の nickname を表示する
    if (this.currentUserName) {
      this.senderNameText = this.currentUserName
    }

    if (this.itemSkuError && this.itemSkuError.length >= 1) {
      dataLayer.push({
        event: 'imp_giftcard_edit__error_exceed'
      })
    }

    this.initializeFeedback()
  },
  methods: {
    onClickChangeCardDesign() {
      dataLayer.push({
        event: 'click_giftcard_edit__card_design_change'
      })
    },
    // 送り主の名前
    validateSenderName(value) {
      return value && value.length !== 0
        ? true
        : 'あなたの名前を入力してください'
    },
    onBlurSenderName() {
      this.checkPreviewAndPaymentButtonAvailable()
      this.updateSenderName()
    },
    // メッセージ
    onFocusMessageArea() {
      this.isMessageAreaActive = true
    },
    onBlurMessageArea() {
      this.isMessageAreaActive = this.isTextInputted(this.messageAreaText)
      this.updateMessage()
    },
    // steppe-control
    countUp(index) {
      const giftCardSessionItemSkuContent =
        this.giftCardSessionItemSkuContents[index]
      const itemCount = parseInt(giftCardSessionItemSkuContent.quantity) + 1
      if (itemCount <= giftCardSessionItemSkuContent.itemSkuQuantityLimit) {
        if (itemCount === giftCardSessionItemSkuContent.itemSkuQuantityLimit) {
          giftCardSessionItemSkuContent.isPlusBtnActive = false
        }
        giftCardSessionItemSkuContent.quantity = itemCount
        giftCardSessionItemSkuContent.isMinusBtnActive = true
        this.updatePriceAndQuantity(giftCardSessionItemSkuContent)
      }
    },
    countDown(index) {
      const giftCardSessionItemSkuContent =
        this.giftCardSessionItemSkuContents[index]
      const itemCount = parseInt(giftCardSessionItemSkuContent.quantity) - 1
      // ギフト詳細からギフトカード作成画面に遷移した場合に、追加枚数によっては上限枚数以上に表示になる場合がある
      // その場合 [-] ボタンのみ有効で、 [+] ボタンは上限枚数未満になるまで有効にならない
      // @see https://github.com/giftee/kitty/issues/1502
      if (itemCount < giftCardSessionItemSkuContent.itemSkuQuantityLimit) {
        giftCardSessionItemSkuContent.isPlusBtnActive = true
      }
      if (itemCount <= 1) {
        giftCardSessionItemSkuContent.isMinusBtnActive = false
        giftCardSessionItemSkuContent.quantity = 1
        this.updatePriceAndQuantity(giftCardSessionItemSkuContent)
      }
      if (1 < itemCount) {
        giftCardSessionItemSkuContent.isMinusBtnActive = true
        giftCardSessionItemSkuContent.quantity = itemCount
        this.updatePriceAndQuantity(giftCardSessionItemSkuContent)
      }
    },
    updatePriceAndQuantity(giftCardSessionItemSkuContent) {
      this.updatePriceAfterChangeQuantity(giftCardSessionItemSkuContent)
      clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(() => {
        this.updateQuantity(
          giftCardSessionItemSkuContent.id,
          giftCardSessionItemSkuContent.quantity
        )
      }, 1000)
    },
    async updateQuantity(
      giftCardSessionItemSkuContentId,
      giftCardSessionItemSkuContentQuantity
    ) {
      try {
        await axios.patch('/api/v1/pickup/gift_card_sessions/quantity', {
          gift_card_session: {
            item_sku_content_id: giftCardSessionItemSkuContentId,
            quantity: giftCardSessionItemSkuContentQuantity
          }
        })
      } catch (error) {
        this.showableErrorFeedback = true
        if (error.response && error.response.status === 404) {
          this.errorMessage =
            '作成中のギフトカードが見つかりませんでした。画面を再読み込みしてください'
          return
        }
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async clickedSuggestedItemSkuAddButton(itemId, itemSkuId) {
      try {
        await axios.post('/api/v1/pickup/gift_card_item_sku_contents/create', {
          item_sku_form: {
            item_id: itemId,
            item_sku_id: itemSkuId,
            quantity: 1,
            submit_type: 'send_gift'
          }
        })
        this.getGiftCardSessionItemSkuContents()
        this.successMessage = 'ギフトを追加しました'
        this.showableSuccessFeedback = true
      } catch (error) {
        this.showableErrorFeedback = true
        if (error.response && error.response.status === 404) {
          this.errorMessage =
            '作成中のギフトカードが見つかりませんでした。画面を再読み込みしてください'
          return
        }
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async updateSenderName() {
      await this.updateGiftCardSessions({
        gift_card_session: {
          sender_name: this.senderNameText
        }
      })
    },
    async updateMessage() {
      await this.updateGiftCardSessions({
        gift_card_session: {
          message: this.messageAreaText
        }
      })
    },
    async updateGiftCardSessions(data) {
      try {
        await axios.patch('/api/v1/pickup/gift_card_sessions', data)
      } catch (error) {
        this.showableErrorFeedback = true
        if (error.response && error.response.status === 404) {
          this.errorMessage =
            '作成中のギフトカードが見つかりませんでした。画面を再読み込みしてください'
          return
        }
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    onBlur(index) {
      const giftCardSessionItemSkuContent =
        this.giftCardSessionItemSkuContents[index]
      if (giftCardSessionItemSkuContent.quantity <= 1) {
        giftCardSessionItemSkuContent.isMinusBtnActive = false
        giftCardSessionItemSkuContent.quantity = 1
      } else {
        giftCardSessionItemSkuContent.isMinusBtnActive = true
      }
      this.updatePriceAfterChangeQuantity(giftCardSessionItemSkuContent)
    },
    enterError() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    enterSuccess() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    showDeleteDraftModal() {
      this.isDeleteDraftModalActive = true
    },
    closeDeleteDraftModal() {
      this.isDeleteDraftModalActive = false
    },
    checkPreviewAndPaymentButtonAvailable() {
      this.isPreviewAndPaymentButtonAvailable =
        this.isTextInputted(this.cardDesignId) &&
        this.isTextInputted(this.senderNameText) &&
        this.giftCardSessionItemSkuContents.length > 0
    },
    isTextInputted(text) {
      if (text === null) {
        return false
      }
      return text.length !== 0
    },
    calcSubTotal(giftCardSessionItemSkuContent) {
      giftCardSessionItemSkuContent.subTotal =
        parseInt(giftCardSessionItemSkuContent.quantity) *
        parseInt(giftCardSessionItemSkuContent.price)
    },
    calcTotalPrice() {
      this.totalPrice = this.giftCardSessionItemSkuContents.reduce(
        (previous, current) => previous + parseInt(current.subTotal),
        0
      )
    },
    updatePriceAfterChangeQuantity(giftCardSessionItemSkuContent) {
      this.calcSubTotal(giftCardSessionItemSkuContent)
      this.calcTotalPrice()
    },
    async getGiftCardSession() {
      this.isGiftCardSessionLoading = true

      try {
        const res = await axios.get(`/api/v1/pickup/gift_card_sessions`)
        this.giftCardSessionId = res.data.giftCardSession.id
        this.cardDesignId = res.data.cardDesignId
        this.cardDesignImageUrl = res.data.cardDesignImageUrl
        if (res.data.giftCardSession.sender_name) {
          this.senderNameText = res.data.giftCardSession.sender_name
        }
        this.messageAreaText = res.data.giftCardSession.message
        this.usableProfileLink = res.data.giftCardSession.usable_profile_link
        this.onBlurSenderName()
        this.onBlurMessageArea()
        this.isGiftCardSessionLoading = false
      } catch (error) {
        this.isGiftCardSessionLoading = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getGiftCardSessionItemSkuContents() {
      this.isItemSkuContentsLoading = true

      try {
        const res = await axios.get(
          `/api/v1/pickup/gift_card_sessions/item_sku_contents`
        )
        this.giftCardSessionItemSkuContents = res.data.data.map(
          itemSkuContent => {
            const hasMultipleItemSkus =
              itemSkuContent.attributes.hasMultipleItemSkus
            return {
              id: itemSkuContent.attributes.id,
              imageUrl: itemSkuContent.attributes.imageUrl,
              itemId: itemSkuContent.attributes.itemId,
              itemName: itemSkuContent.attributes.itemName,
              itemSkuId: itemSkuContent.attributes.itemSkuId,
              itemSkuName: hasMultipleItemSkus
                ? itemSkuContent.attributes.itemSkuName
                : '',
              hasMultipleItemSkus,
              brandName: itemSkuContent.attributes.brandName,
              expiredDate: itemSkuContent.attributes.expiredDate,
              subTotal: itemSkuContent.attributes.subTotal,
              quantity: itemSkuContent.attributes.quantity,
              isMinusBtnActive: itemSkuContent.attributes.isMinusBtnActive,
              isPlusBtnActive: itemSkuContent.attributes.isPlusBtnActive,
              price: itemSkuContent.attributes.price,
              isIncludingTax: itemSkuContent.attributes.isIncludingTax,
              isFulfillment: itemSkuContent.attributes.isFulfillment,
              itemSkuQuantityLimit:
                itemSkuContent.attributes.itemSkuQuantityLimit
            }
          }
        )
        this.calcTotalPrice()
        this.checkPreviewAndPaymentButtonAvailable()
        if (this.giftCardSessionItemSkuContents.length > 0) {
          this.hasDeletedAllSelectedItems = false
        }
        this.isItemSkuContentsLoading = false
      } catch (error) {
        this.isItemSkuContentsLoading = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    onClickedPrimaryDeleteDraftModal() {
      this.closeDeleteDraftModal()
      this.submitForm('delete_draft')
    },
    initializeFeedback() {
      if (this.flashNotice) {
        this.successMessage = this.flashNotice
        this.showableSuccessFeedback = true
      }
    },
    submitForm(submitValue) {
      const form = document.gift_card_edit_form
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'gift_card_form[submit_type]'
      input.value = submitValue
      form.appendChild(input)
      form.submit()
    },
    clickDeleteButton(giftCardSessionItemSkuContentId, selectedItemSkuId) {
      this.selectedItemSkuContentId = giftCardSessionItemSkuContentId
      this.selectedItemSkuId = selectedItemSkuId
      this.isDeleteItemSkuModalActive = true
    },
    closeDeleteItemSkuModal() {
      this.isDeleteItemSkuModalActive = false
    },
    async onClickedPrimaryDeleteItemSkuModal() {
      this.isItemSkuContentsLoading = true
      this.isDeleteItemSkuModalActive = false

      dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          items: [
            {
              item_id: this.selectedItemSkuId
            }
          ]
        }
      })

      try {
        const res = await axios.delete(
          `/api/v1/pickup/gift_card_sessions/${this.selectedItemSkuContentId}`
        )
        this.giftCardSessionItemSkuContents = res.data.data.map(
          itemSkuContent => {
            const hasMultipleItemSkus =
              itemSkuContent.attributes.hasMultipleItemSkus
            return {
              id: itemSkuContent.attributes.id,
              imageUrl: itemSkuContent.attributes.imageUrl,
              itemId: itemSkuContent.attributes.itemId,
              itemName: itemSkuContent.attributes.itemName,
              itemSkuId: itemSkuContent.attributes.itemSkuId,
              itemSkuName: hasMultipleItemSkus
                ? itemSkuContent.attributes.itemSkuName
                : '',
              hasMultipleItemSkus,
              brandName: itemSkuContent.attributes.brandName,
              expiredDate: itemSkuContent.attributes.expiredDate,
              subTotal: itemSkuContent.attributes.subTotal,
              quantity: itemSkuContent.attributes.quantity,
              isMinusBtnActive: itemSkuContent.attributes.isMinusBtnActive,
              isPlusBtnActive: itemSkuContent.attributes.isPlusBtnActive,
              price: itemSkuContent.attributes.price,
              isIncludingTax: itemSkuContent.attributes.isIncludingTax,
              isFulfillment: itemSkuContent.attributes.isFulfillment,
              itemSkuQuantityLimit:
                itemSkuContent.attributes.itemSkuQuantityLimit
            }
          }
        )
        this.calcTotalPrice()
        this.checkPreviewAndPaymentButtonAvailable()
        this.hasDeletedAllSelectedItems =
          this.giftCardSessionItemSkuContents.length === 0
        this.selectedItemSkuContentId = null
        this.selectedItemSkuId = null
        this.isItemSkuContentsLoading = false
      } catch (error) {
        this.selectedItemSkuContentId = null
        this.selectedItemSkuId = null
        this.isItemSkuContentsLoading = false
        this.showableErrorFeedback = true
        if (error.response && error.response.status === 404) {
          this.errorMessage =
            '作成中のギフトカードが見つかりませんでした。画面を再読み込みしてください'
          return
        }
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    findItemSkuError(itemSkuId) {
      return this.itemSkuError.find(error => error.item_sku_id === itemSkuId)
    },
    showSendToMultiplePeopleModal() {
      this.sendToMultiplePeopleModalDisplayable = true
    },
    hideSendToMultiplePeopleModal() {
      this.sendToMultiplePeopleModalDisplayable = false
    },
    // FIXME(gift_card_preview_optional): ABテスト決着後、不要なら削除する
    showPreviewModal() {
      dataLayer.push({
        event: 'click_giftcard_edit__preview_modal'
      })
      this.previewModalDisplayable = true
    },
    hidePreviewModal() {
      this.previewModalDisplayable = false
    },
    clickContinueButton() {
      dataLayer.push({
        event: 'click_giftcard_edit__draft_order',
        toggle_display: this.showUsableProfileLink,
        toggle_onoff: this.usableProfileLink,
        gift_card_session_id: this.giftCardSessionId
      })
    },
    clickLoginAndContinueButton() {
      dataLayer.push({
        event: 'signin_click',
        button: 'giftcard_edit'
      })
    },
    // FIXME(gift_card_preview_optional): ABテスト決着後、不要なら削除する
    clickUsableProfileLinkSwitch() {
      this.usableProfileLink = !this.usableProfileLink
      dataLayer.push({
        event: 'click_giftcard_edit__profile_link_toggle',
        toggle_onoff: this.usableProfileLink
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.gift-card-session-loading {
  padding-top: 16px;
  padding-bottom: 8px;
}

.select-item__box {
  &:not(:first-child) {
    padding-top: 16px;
  }

  &:not(:last-child) {
    border-bottom: 1px $gray-2 solid;
    padding-bottom: 16px;
  }
}

.gift-card-edit-container {
  background-color: $white;
}

.gift-card {
  &__head {
    max-width: $base-contents-width;
    margin: 12px auto;
  }

  &__main-wrapper {
    max-width: $base-contents-width;
    margin: 0 auto;
    padding: 32px 24px;
  }

  &__main {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 24px;
  }

  &__main-left,
  &__main-right {
    min-width: 0;
  }

  &__main-right {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  &__gray-border {
    height: 16px;
    background-color: $gray-3;
  }

  // gift-card__head 内コンテンツ

  &__flow-line-area {
    margin: 24px 0;

    display: flex;
    justify-content: center;
  }

  &__headline {
    margin: 24px 0;
  }

  // gift-card__main 内コンテンツ
  &__receiver-info-area-wrapper {
    margin: 0 auto;
    padding: 0 16px;
    max-width: $base-contents-width;
  }

  &__receiver-info-area {
    margin: 24px 0;

    display: flex;
    align-items: center;
  }

  &__receiver-name-area {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  &__receiver-name {
    padding-right: 4px;
    text-overflow: ellipsis;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    flex: 1;
  }

  &__alert-box {
    margin-top: 40px;

    text-align: left;
    white-space: pre-line;

    li {
      list-style: inside;
    }
  }

  &__card-design {
    text-align: left;
    flex-basis: 50%;
    display: flex;
    justify-content: center;
  }

  &__input-form {
    text-align: left;
    flex-basis: 50%;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  // gift-card__card-design 内コンテンツ

  &__required_caution {
    color: $danger;
    text-align: left;
  }

  &__title {
    margin-bottom: 16px;
  }

  &__select-item-title-area {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__send-to-multiple-people {
    &__area {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 4px;
      cursor: pointer;
    }

    &__link-text {
      font-size: 1.4rem;
    }
  }

  &__first_title {
    margin-bottom: 16px;
  }

  &__card-design-area {
    width: 100%;
  }

  &__card-design-content {
    width: 342px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    position: relative;
  }

  &__card-design-image {
    width: 100%;
    height: auto;
  }

  &__reset-link-area {
    display: flex;
    justify-content: center;
  }

  // FIXME(gift_card_preview_optional): ABテスト決着後、不要なら削除する
  &__save-button-area {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__preview-section {
    width: 100%;
  }

  &__usable-profile-link-area {
    margin-bottom: 32px;
  }

  &__preview-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  // SP幅のみ表示する区切り線
  &__preview-section-border {
    @media screen and (min-width: $base-contents-width) {
      display: none;
    }

    @media screen and (max-width: ($base-contents-width - 1)) {
      // gift-card__main-wrapper の左右paddingを打ち消して画面幅いっぱいに広げる
      margin: 0 -16px 24px;
    }
  }

  &__card-design-change-link {
    position: absolute;
    right: 16px;
    bottom: 16px;

    text-align: center;

    .text-link {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 124px;
      height: 48px;
      box-sizing: border-box;
      border: 2px solid $gray-2;
      border-radius: 24px;
      background-color: rgba($white, 0.8);
      color: $black;
      font-weight: bold;
    }

    a {
      display: inline;
    }
  }

  &__sync-outline {
    display: flex;
    align-items: center;
    padding-left: 32px;
    color: $black;
    font-size: 14px;
    line-height: 1.6;
    background-position: left center;

    @include bg-settings(
      '../../../../../images/icons/sync-outline-black.svg',
      transparent,
      24px,
      24px
    );
  }

  // gift-card__input-form 内コンテンツ

  &__input-message-area,
  &__input-sender-name-area,
  &__select-item-area {
    .item-sku-contents-loading {
      width: 32px;
      margin: auto;
    }
  }

  &__sender-name-with-icon {
    display: flex;
    align-items: center;
  }

  &__sender-name-input {
    width: 100%;
  }

  &__sender-name-box-list {
    margin-top: 16px;
    font-size: 1rem;
    line-height: 1.6;
    padding: 8px;
    max-width: 100%;
    border-radius: 4px;
    color: $gray-1;
    background-color: $gray-3;
    border: 1px solid $gray-2;
  }

  &__account-icon {
    margin-right: 8px;
  }

  &__input-message-description {
    text-align: left;
    margin-bottom: 16px;
  }

  &__ticket-box-item {
    display: flex;
    flex-direction: column;
  }

  &__ticket-box-buttons {
    margin-top: 24px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .delete_button {
      display: flex;
      align-items: center;
      height: 26px;
      padding: 0 8px;
    }
  }

  &__ticket-box-errors {
    @extend %minimal;
    color: $danger;
    white-space: break-spaces;
    margin-top: 24px;
  }

  &__adding-gift-area--top {
    margin-top: 16px;
    padding: 24px 0;

    border-top: solid 1px $gray-2;
    border-bottom: solid 1px $gray-2;
    display: flex;
    justify-content: center;
  }

  &__adding-gift-area--bottom {
    @extend .gift-card__adding-gift-area--top;
    border-top: 0;
    margin-top: 0;
  }

  // gift-card__foot-content 内コンテンツ

  &__foot-content {
    margin: auto;
    padding: 24px 16px;
    max-width: $small-contents-width;
  }

  &__bold-title {
    font-weight: bold;
  }

  &__total-price-area {
    margin-bottom: 40px;
    display: flex;
    justify-content: space-between;
  }

  &__total-price-label {
    &__title {
      @extend %subhead-bold;
    }

    &__including-tax {
      @extend %body;
      line-height: 1.6;
    }
  }

  // 画面の横幅が 768px (iPad mini) 未満の場合
  @media screen and (max-width: ($base-contents-width - 1)) {
    &__headline__title {
      max-width: $small-contents-width;
      margin: auto;
    }

    &__required_caution {
      max-width: $small-contents-width;
      margin: auto;
    }

    &__main-wrapper {
      padding: 24px 16px;
    }

    &__main {
      display: flex;
      flex-direction: column;
      gap: 40px;
      max-width: $small-contents-width;
      margin: auto;
    }

    &__card-design {
      display: block;
    }

    &__head-title {
      text-align: left;
    }

    &__card-design-content {
      width: 100%;
    }
  }
}
</style>
